import { useCallback } from "react";

import UTIF from "utif";

import { type UploadError } from "@/common/UploadModal/types/errorTypes";
import { type UploadErrorManager } from "@/common/UploadModal/utils/errorManager";
import { getPdfPageCount } from "@/helper/splitPage/getPdfPageCount";

interface ValidateUploadPageLimitResult {
  nextAcceptedFiles: File[];
  nextUploadErrors: UploadError[];
}

export const getFilePageCount = async (file: File): Promise<number> => {
  switch (file.type) {
    case "application/pdf":
      return getPdfPageCount(file);
    case "image/tiff": {
      const arrayBuffer = await file.arrayBuffer();
      const ifds = UTIF.decode(new Uint8Array(arrayBuffer));
      return ifds.length;
    }
    default:
      return 1;
  }
};

export const useUploadPageLimitValidation = (
  pageLimit: number
): {
  validateUploadPageLimit: (files: File[], errorManager: UploadErrorManager) => Promise<ValidateUploadPageLimitResult>;
} => {
  const validateUploadPageLimit = useCallback(
    async (files: File[], errorManager: UploadErrorManager): Promise<ValidateUploadPageLimitResult> => {
      const nextAcceptedFiles: File[] = [];
      const nextUploadErrors: UploadError[] = [];

      for (const acceptedFile of files) {
        try {
          const pageCount = await getFilePageCount(acceptedFile);
          if (pageCount > pageLimit) {
            const pageLimitError = errorManager.createExceedsPageLimitError(acceptedFile.name, pageLimit);
            nextUploadErrors.push(pageLimitError);
            continue;
          }

          nextAcceptedFiles.push(acceptedFile);
        } catch {
          const uploadError = errorManager.createUploadError(acceptedFile.name);
          nextUploadErrors.push(uploadError);
        }
      }

      return { nextAcceptedFiles, nextUploadErrors };
    },
    [pageLimit]
  );

  return { validateUploadPageLimit };
};
